import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Alert, AlertDescription } from './ui/alert';
import { Truck, Upload, Info } from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fonction pour obtenir la date/heure actuelle au format datetime-local
const getCurrentDateTime = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const EnRouteModal = ({ open, onClose, operation, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  
  // Route info - Pré-rempli avec la date/heure actuelle
  const [departureTime, setDepartureTime] = useState(getCurrentDateTime());
  const [routeComment, setRouteComment] = useState('');
  
  // Optional documents
  const [photosDepart, setPhotosDepart] = useState([]);

  // Réinitialiser la date/heure à l'ouverture du modal
  useEffect(() => {
    if (open) {
      setDepartureTime(getCurrentDateTime());
    }
  }, [open]);

  const handlePhotosChange = (files) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;
    
    const oversized = fileArray.filter(f => f.size > 10 * 1024 * 1024);
    if (oversized.length > 0) {
      toast({
        title: "Fichiers trop volumineux",
        description: "La taille maximale par photo est de 10MB",
        variant: "destructive"
      });
      return;
    }
    
    setPhotosDepart(fileArray);
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      // Step 1: Create logistics info (only if data provided)
      if (departureTime || routeComment) {
        const logisticsData = {
          workflowStep: 'charge_en_route',
          departureTime: departureTime ? new Date(departureTime).toISOString() : null,
          routeComment: routeComment || null
        };
        
        await axios.post(
          `${API}/operations/${operation.id}/logistics`,
          logisticsData
        );
      }
      
      // Step 2: Upload photos if provided
      if (photosDepart.length > 0) {
        const photoPromises = photosDepart.map(photo => {
          const formData = new FormData();
          formData.append('file', photo);
          formData.append('documentType', 'photos_depart');
          
          return axios.post(
            `${API}/operations/${operation.id}/documents`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );
        });
        
        await Promise.all(photoPromises);
      }
      
      // Step 3: Change status to CHARGE_EN_ROUTE
      const { changeOperationStatus } = await import('../utils/statusChangeHelper');
      await changeOperationStatus(operation.id, 'charge_en_route', {
        departure_time: departureTime || null,
        route_comment: routeComment || null
      });
      
      toast({
        title: "✅ En route",
        description: "L'opération est maintenant en cours de livraison"
      });
      
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
      
    } catch (error) {
      console.error('Error submitting en route:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Une erreur est survenue",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    setLoading(true);
    
    try {
      // Just change status without any data
      const { changeOperationStatus } = await import('../utils/statusChangeHelper');
      await changeOperationStatus(operation.id, 'charge_en_route');
      
      toast({
        title: "✅ En route",
        description: "L'opération est maintenant en cours de livraison"
      });
      
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
      
    } catch (error) {
      console.error('Error changing status:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Une erreur est survenue",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Départ en Route</DialogTitle>
          <DialogDescription>
            Informations optionnelles - Vous pouvez passer cette étape
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <Alert className="bg-blue-50 border-blue-200">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-900">
              Cette étape est <strong>optionnelle</strong>. Vous pouvez renseigner des informations complémentaires ou passer directement au statut "En route".
            </AlertDescription>
          </Alert>
          
          <div className="flex items-center gap-2 mb-4">
            <Truck className="w-5 h-5 text-[#1db584]" />
            <h3 className="text-lg font-semibold">Informations de Départ</h3>
          </div>
          
          <div className="space-y-3">
            <div>
              <Label htmlFor="departureTime">Heure de départ réelle (optionnel)</Label>
              <Input
                id="departureTime"
                type="datetime-local"
                value={departureTime}
                onChange={(e) => setDepartureTime(e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="routeComment">Commentaires terrain (optionnel)</Label>
              <Textarea
                id="routeComment"
                placeholder="ex: Départ avec 30 min de retard, Scellé posé numéro XXX..."
                value={routeComment}
                onChange={(e) => setRouteComment(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center gap-2 mb-2">
              <Upload className="w-5 h-5 text-[#1db584]" />
              <h3 className="text-lg font-semibold">Documents Optionnels</h3>
            </div>
            
            <div className="border rounded-lg p-3">
              <Label htmlFor="photosDepart" className="flex items-center gap-2">
                📸 Photos avant départ (optionnel)
              </Label>
              <p className="text-sm text-gray-500 mb-2">
                Photos du scellé, chargement final, camion fermé, etc.
              </p>
              
              <Input
                id="photosDepart"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handlePhotosChange(e.target.files)}
                className="cursor-pointer"
              />
              
              {photosDepart.length > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  {photosDepart.length} photo(s) sélectionnée(s)
                </p>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleSkip} 
              disabled={loading}
            >
              Passer cette étape
            </Button>
            
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? 'Traitement...' : 'Enregistrer et continuer'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EnRouteModal;
